import { useState, useEffect, useRef, useCallback } from "react";

interface Language {
  code: string;
  flag: string;
  name: string;
  native: string;
}

const LANGUAGES: Language[] = [
  { code: "en",    flag: "\u{1F1FA}\u{1F1F8}", name: "English",    native: "English"   },
  { code: "es",    flag: "\u{1F1EA}\u{1F1F8}", name: "Spanish",    native: "Espa\u{00F1}ol"   },
  { code: "fr",    flag: "\u{1F1EB}\u{1F1F7}", name: "French",     native: "Fran\u{00E7}ais"  },
  { code: "de",    flag: "\u{1F1E9}\u{1F1EA}", name: "German",     native: "Deutsch"   },
  { code: "pt",    flag: "\u{1F1E7}\u{1F1F7}", name: "Portuguese", native: "Portugu\u{00EA}s" },
  { code: "it",    flag: "\u{1F1EE}\u{1F1F9}", name: "Italian",    native: "Italiano"  },
  { code: "ru",    flag: "\u{1F1F7}\u{1F1FA}", name: "Russian",    native: "\u{0420}\u{0443}\u{0441}\u{0441}\u{043A}\u{0438}\u{0439}"   },
  { code: "ar",    flag: "\u{1F1F8}\u{1F1E6}", name: "Arabic",     native: "\u{0627}\u{0644}\u{0639}\u{0631}\u{0628}\u{064A}\u{0629}"   },
  { code: "ja",    flag: "\u{1F1EF}\u{1F1F5}", name: "Japanese",   native: "\u{65E5}\u{672C}\u{8A9E}"     },
  { code: "zh-CN", flag: "\u{1F1E8}\u{1F1F3}", name: "Chinese",    native: "\u{4E2D}\u{6587}"       },
  { code: "ko",    flag: "\u{1F1F0}\u{1F1F7}", name: "Korean",     native: "\u{D55C}\u{AD6D}\u{C5B4}"     },
  { code: "sw",    flag: "\u{1F1F9}\u{1F1FF}", name: "Kiswahili",  native: "Kiswahili" },
];

function setGoogTransCookie(langCode: string) {
  const value = "/en/" + langCode;
  const hostname = window.location.hostname;
  document.cookie = "googtrans=" + value + ";path=/;domain=." + hostname;
  document.cookie = "googtrans=" + value + ";path=/";
}

function clearGoogTransCookies() {
  const hostname = window.location.hostname;
  const expired = "expires=Thu, 01 Jan 1970 00:00:00 UTC";
  ["", "domain=" + hostname, "domain=." + hostname, "domain=www." + hostname].forEach(
    function (d) {
      ["path=/", ""].forEach(function (p) {
        document.cookie = ["googtrans=", expired, p, d].filter(Boolean).join("; ");
      });
    }
  );
}

export default function LanguageSelector() {
  const [currentCode] = useState<string>(
    () => localStorage.getItem("rv_lang") || "en"
  );
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const current = LANGUAGES.find((l) => l.code === currentCode) || LANGUAGES[0];
  const isTranslated = currentCode !== "en";

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setIsOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, []);

  const selectLanguage = useCallback(
    (lang: Language) => {
      setIsOpen(false);
      if (lang.code === currentCode) return;

      if (lang.code === "en") {
        localStorage.removeItem("rv_lang");
        clearGoogTransCookies();
      } else {
        localStorage.setItem("rv_lang", lang.code);
        setGoogTransCookie(lang.code);
      }

      window.location.reload();
    },
    [currentCode]
  );

  const S: Record<string, React.CSSProperties> = {
    wrapper: { position: "relative", userSelect: "none" },
    trigger: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: "8px 13px",
      background: isOpen
        ? "rgba(0,180,216,0.18)"
        : isTranslated
          ? "rgba(0,180,216,0.15)"
          : "rgba(0,180,216,0.08)",
      border:
        "1px solid " +
        (isOpen || isTranslated ? "#00B4D8" : "rgba(0,180,216,0.25)"),
      borderRadius: 10,
      cursor: "pointer",
      color: "#fff",
      fontFamily: "inherit",
      transition: "all 0.2s",
    },
    tFlag: { fontSize: 20, lineHeight: 1 },
    tText: { display: "flex", flexDirection: "column" as const, gap: 1 },
    tName: { fontSize: 13, fontWeight: 500, color: "#fff" },
    tCode: {
      fontSize: 10,
      color: "#00B4D8",
      letterSpacing: "0.5px",
      fontFamily: "monospace",
    },
    chevron: {
      fontSize: 10,
      color: "#8899AA",
      marginLeft: 4,
      display: "inline-block",
      transform: isOpen ? "rotate(180deg)" : "none",
      transition: "transform 0.22s",
    },
    dropdown: {
      position: "absolute" as const,
      top: "calc(100% + 8px)",
      right: 0,
      width: 256,
      background: "#0D1F38",
      border: "1px solid rgba(0,180,216,0.25)",
      borderRadius: 14,
      boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
      overflow: "hidden",
      zIndex: 1000,
    },
    dHeader: {
      padding: "11px 16px 8px",
      fontFamily: "monospace",
      fontSize: 9,
      letterSpacing: "2px",
      color: "#00B4D8",
      textTransform: "uppercase" as const,
      borderBottom: "1px solid rgba(0,180,216,0.1)",
    },
    list: { padding: "6px 0", maxHeight: 380, overflowY: "auto" as const },
    note: {
      padding: "8px 16px 10px",
      fontSize: 10,
      color: "#556677",
      borderTop: "1px solid rgba(0,180,216,0.08)",
      fontStyle: "italic",
    },
  };

  return (
    <div ref={ref} style={S.wrapper} className="notranslate">
      <button
        style={S.trigger}
        onClick={() => setIsOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label="Select language"
      >
        <span style={S.tFlag}>{current.flag}</span>
        <span style={S.tText}>
          <span style={S.tName}>{current.name}</span>
          <span style={S.tCode}>
            {currentCode === "zh-CN" ? "ZH" : currentCode.toUpperCase()}
          </span>
        </span>
        <span style={S.chevron}>{"▼"}</span>
      </button>

      {isOpen && (
        <div style={S.dropdown} role="listbox" className="notranslate">
          <div style={S.dHeader}>Select Language</div>
          <div style={S.list}>
            {LANGUAGES.map((lang) => {
              const active = lang.code === currentCode;
              return (
                <div
                  key={lang.code}
                  role="option"
                  aria-selected={active}
                  onClick={() => selectLanguage(lang)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "10px 16px",
                    cursor: "pointer",
                    background: active
                      ? "rgba(0,180,216,0.12)"
                      : "transparent",
                    transition: "background 0.15s",
                  }}
                  onMouseEnter={(e) => {
                    if (!active)
                      (e.currentTarget as HTMLDivElement).style.background =
                        "rgba(0,180,216,0.08)";
                  }}
                  onMouseLeave={(e) => {
                    if (!active)
                      (e.currentTarget as HTMLDivElement).style.background =
                        "transparent";
                  }}
                >
                  <span style={{ fontSize: 22, lineHeight: 1, flexShrink: 0 }}>
                    {lang.flag}
                  </span>
                  <span
                    style={{
                      display: "flex",
                      flexDirection: "column" as const,
                      gap: 1,
                      flex: 1,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 13,
                        fontWeight: 500,
                        color: active ? "#00B4D8" : "#fff",
                      }}
                    >
                      {lang.name}
                    </span>
                    <span style={{ fontSize: 11, color: "#8899AA" }}>
                      {lang.native}
                    </span>
                  </span>
                  {active && (
                    <span style={{ fontSize: 13, color: "#00B4D8" }}>
                      {"✓"}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
          <div style={S.note}>Powered by Google Translate</div>
        </div>
      )}
    </div>
  );
}
